import { LayerTypeId } from "../../generated/enums"
import { arrayMax, arrayMin, groupBy } from "../../utils/array"
import { IDataLayer, IDatalayerKeyItem, IDatalayerViewerFactory, INumericValuesViewerOptions, ZoneTextLocation } from "../types"
import { NumericValuesViewer } from "./numeric"

/**
 * This is used specifically for dynamic scale TerraMap layers
 */
export class TerramapDynamicLayerViewer extends NumericValuesViewer {

    static factory(type: LayerTypeId, name: string, options?: INumericValuesViewerOptions): IDatalayerViewerFactory {

        return {
            typeId: type,
            name,
            createViewer: layer => new TerramapDynamicLayerViewer(layer, options)
        }

    }


    private values: number[]
    private colors: number[][]
    private useIndex = false

    private minmax: { min: number, max: number }


    protected setupMinMaxZoneValues() {

        const values = super.setupMinMaxZoneValuesReturnValues()

        const distinct = this.values = Array.from(new Set(values)).sort((a, b) => a - b)

        const colors = this.colors = this.colorProvider.getColors() || []

        // Dynamic scale layers should always have the same number of distinct values, or less
        // If that's the case, we used indexed colours
        if (distinct.length <= colors.length) {
            this.useIndex = true
        }

        this.minmax = getMinMaxValues(this._layer.datalayer.Points, distinct)

        return values

    }


    public getValueFillColor(value: number, alpha: number = 1) {

        if (this.useIndex) {

            const index = this.values.indexOf(value)

            if ((index >= 0) && (index < this.colors.length)) {
                return this.colors[index]
            }

        }

        return super.getValueFillColor(value, alpha)

    }

    getValueText(value: number, location?: ZoneTextLocation): string {

        if (this.useIndex) {

            const { values, minmax } = this

            const getValueAtIndex = (index: number) => {
                return (index >= values.length)
                    ? minmax.max
                    : values[index]
            }

            const index = values.indexOf(value)

            const text = (index >= 0)
                ? values[index] + " - " + getValueAtIndex(index + 1)
                : (!!value || value === 0) && !isNaN(value) ? value.toString() : ""

            return text

        }

        return super.getValueText(value, location)

    }

    getKey() {

        const key = super.getKey()

        const points = this._layer.datalayer.Points

        const minmax = this.minmax ?? { min: points?.Min, max: points?.Max }

        const items = getKey(key.layerKey, minmax)

        return {
            ...key,
            typeKey: items,
            layerKey: items,
            fixed: true
        }

    }

}

function getKey(key: IDatalayerKeyItem[], minmax: { min: number, max: number }) {

    if (!key) return key

    const groups = groupBy(key, c => c.color)
        .map(g => ({ color: g.key, min: arrayMin(g.values, v => Number(v.value)), max: arrayMax(g.values, v => Number(v.value)) }))
        .sort((a, b) => a.min - b.min)

    const last = (groups.length - 1)

    const updatedKey = groups.map((g, index) => {

        let text = ""

        const { min, max } = g

        if (index == last) {
            text = ((min == max) && (max < minmax.max))
                ? `${min} - ${minmax.max}`
                : (min == max)
                    ? `>= ${min}` : `${min} - ${max}`
        }
        else {
            const next = groups[index + 1].min
            text = `${min} - ${next}`
        }

        return {
            color: g.color,
            text,
            value: min
        }

    })

    return updatedKey

}

/**
 * Returns the minimum and maximum values found in a distribution, rounded to a "reasonable" number of decimals
 *
 * @param points Points distribution
 * @param type Layer type
 */
function getMinMaxValues(points: IDataLayer["Points"], values: number[]): { min: number, max: number } {

    if (!points) {
        return undefined
    }

    let min = points.Min
    let max = points.Max

    // Retain only the decimal parts, up to 3 decimals at most
    values = values.map(v => Math.round((v - Math.floor(v)) * 100) / 100)

    // Retain the maximum number of decimals required, for all values
    const r = arrayMax(
        values,
        // This yields the number of decimals required to describe each value, as a power of 10 (1: no decimal, 10: 1 decimal, 100: 2 decimals...)
        d => d ? Math.pow(10, Math.ceil(Math.log10(1 / d))) : 1
    )

    min = Math.floor(min * r) / r
    max = Math.ceil(max * r) / r

    return { min, max }

}
